import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  TextField,
  Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { APIProvider, AdvancedMarker, Map, Marker, Pin, useMapsLibrary } from '@vis.gl/react-google-maps';
import projectService, { ProjectAddressPreview } from '../../services/projectService';

type LatLng = { lat: number; lng: number };

type LocationPickerDialogProps = {
  open: boolean;
  projectCode: string;
  currentLat?: number;
  currentLng?: number;
  onClose: () => void;
  onApply: (preview: ProjectAddressPreview, previewKey?: string) => void;
};

const DEFAULT_CENTER: LatLng = { lat: 4.711, lng: -74.0721 };

const isLikelyPlusCode = (raw: string) => {
  const value = raw.trim().toUpperCase();
  if (!value.includes('+')) return false;
  const token = value.split(/\s+/)[0] || '';
  const normalized = token.replace(/[^0-9A-Z+]/g, '');
  if (!normalized.includes('+')) return false;
  const [left, right] = normalized.split('+');
  if (!left || !right) return false;
  if (left.length + right.length < 6) return false;
  return /^[23456789CFGHJMPQRVWX]+$/.test(left) && /^[23456789CFGHJMPQRVWX]+$/.test(right);
};

const eventLatLngToLiteral = (event: any): LatLng | null => {
  const latLng = event?.detail?.latLng || event?.latLng;
  if (!latLng) return null;
  const lat = typeof latLng.lat === 'function' ? latLng.lat() : latLng.lat;
  const lng = typeof latLng.lng === 'function' ? latLng.lng() : latLng.lng;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
};

export const LocationPickerDialog: React.FC<LocationPickerDialogProps> = (props) => {
  const apiKey = (process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string | undefined)?.trim() || '';
  const mapId = (process.env.REACT_APP_GOOGLE_MAPS_MAP_ID as string | undefined)?.trim() || '';

  if (!apiKey) {
    return (
      <Dialog open={props.open} onClose={props.onClose} fullWidth maxWidth="md">
        <DialogTitle>Seleccionar ubicación</DialogTitle>
        <DialogContent sx={{ pt: 5 }}>
          <Typography variant="body2" color="error">
            Búsqueda no disponible: falta REACT_APP_GOOGLE_MAPS_API_KEY en el build del frontend.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={props.onClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <APIProvider apiKey={apiKey} language="es" libraries={mapId ? ['places', 'marker'] : ['places']}>
      <LocationPickerDialogInner {...props} mapId={mapId || undefined} />
    </APIProvider>
  );
};

type LocationPickerDialogInnerProps = LocationPickerDialogProps & { mapId?: string };

const LocationPickerDialogInner: React.FC<LocationPickerDialogInnerProps> = ({
  open,
  projectCode,
  currentLat,
  currentLng,
  onClose,
  onApply,
  mapId,
}: LocationPickerDialogInnerProps) => {
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const searchAnchorRef = useRef<HTMLDivElement | null>(null);
  const fetchIdRef = useRef(0);
  const reverseGeocodeIdRef = useRef(0);

  const placesLib = useMapsLibrary('places') as any;
  const placesSearchAvailable = Boolean(placesLib?.AutocompleteSuggestion?.fetchAutocompleteSuggestions);

  const initialCenter = useMemo<LatLng>(() => {
    if (Number.isFinite(currentLat) && Number.isFinite(currentLng)) {
      return { lat: currentLat as number, lng: currentLng as number };
    }
    return DEFAULT_CENTER;
  }, [currentLat, currentLng]);

  const [center, setCenter] = useState<LatLng>(initialCenter);
  const [marker, setMarker] = useState<LatLng>(initialCenter);
  const [zoom, setZoom] = useState<number>(16);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPlaceName, setSelectedPlaceName] = useState<string | null>(null);
  const [selectedPlaceAddress, setSelectedPlaceAddress] = useState<string | null>(null);

  const [preview, setPreview] = useState<ProjectAddressPreview | null>(null);
  const [previewKey, setPreviewKey] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [placesStatusMessage, setPlacesStatusMessage] = useState<string>('Cargando búsqueda...');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [suggestionsForQuery, setSuggestionsForQuery] = useState<string>('');

  const updateSelectedFromCoords = useCallback((point: LatLng) => {
    const googleMaps = (globalThis as any)?.google?.maps;
    if (!googleMaps?.Geocoder) return;

    const requestId = ++reverseGeocodeIdRef.current;
    const geocoder = new googleMaps.Geocoder();
    geocoder.geocode({ location: point }, (results: any[], status: string) => {
      if (reverseGeocodeIdRef.current !== requestId) return;
      if (status !== 'OK' || !Array.isArray(results) || results.length === 0) return;

      const formatted = String(results[0]?.formatted_address || '').trim();
      if (!formatted) return;

      const parts = formatted
        .split(',')
        .map((part: string) => part.trim())
        .filter(Boolean);
      const title = parts[0] || formatted;
      const subtitle = parts.length > 1 ? parts.slice(1).join(', ') : '';

      setSelectedPlaceName(title || null);
      setSelectedPlaceAddress(subtitle || null);
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    setCenter(initialCenter);
    setMarker(initialCenter);
    setZoom(16);
    setSearchQuery('');
    setSelectedPlaceName(null);
    setSelectedPlaceAddress(null);
    setPreview(null);
    setPreviewKey(undefined);
    setLoading(false);
    setError(null);
    setSuggestions([]);
    setSuggestionsOpen(false);
    setSuggestionsForQuery('');
  }, [open, initialCenter]);

  useEffect(() => {
    if (!open) return;
    const fetchSuggestions = placesLib?.AutocompleteSuggestion?.fetchAutocompleteSuggestions;
    setPlacesStatusMessage(
      fetchSuggestions
        ? 'Puedes buscar por dirección, barrio, lugar conocido o Plus Code.'
        : 'Búsqueda no disponible: Places (nuevo) no está disponible. Verifica que Places API esté habilitada para esta key.'
    );
  }, [open, placesLib]);

  const doPreview = useCallback(
    async (input: string, opts?: { preserveMapPosition?: boolean }) => {
      const trimmed = input.trim();
      if (!trimmed) {
        setError('Ingresa un Plus Code o una búsqueda válida.');
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const nextPreview = await projectService.previewProjectAddress(projectCode, trimmed);
        setPreview(nextPreview);
        setPreviewKey(nextPreview.plus_code?.trim() || trimmed);
        if (nextPreview.lat != null && nextPreview.lng != null) {
          const point = { lat: nextPreview.lat, lng: nextPreview.lng };
          if (!opts?.preserveMapPosition) {
            setMarker(point);
            setCenter(point);
            setZoom(16);
          }
          updateSelectedFromCoords(point);
        }
      } catch (err: any) {
        const status = err?.response?.status;
        const detail =
          err?.response?.data?.detail || (typeof err?.response?.data === 'string' ? err.response.data : null);
        setError(detail ? `No se pudo derivar la ubicación (${status ?? 'error'}): ${detail}` : 'No se pudo derivar la ubicación');
      } finally {
        setLoading(false);
      }
    },
    [projectCode, updateSelectedFromCoords]
  );

  const doPreviewFromCoords = useCallback(
    async (point: LatLng) => {
      setLoading(true);
      setError(null);
      try {
        const nextPreview = await projectService.previewProjectAddressByCoords(projectCode, point.lat, point.lng);
        setPreview(nextPreview);
        setPreviewKey(nextPreview.plus_code?.trim() || `${point.lat},${point.lng}`);
      } catch (err: any) {
        const status = err?.response?.status;
        const detail =
          err?.response?.data?.detail || (typeof err?.response?.data === 'string' ? err.response.data : null);
        setError(detail ? `No se pudo derivar la ubicación (${status ?? 'error'}): ${detail}` : 'No se pudo derivar la ubicación');
      } finally {
        setLoading(false);
      }
    },
    [projectCode]
  );

  const commitMarker = useCallback(
    async (point: LatLng) => {
      setMarker(point);
      await doPreviewFromCoords(point);
    },
    [doPreviewFromCoords]
  );

  const resolvePlacePrediction = useCallback(
    async (prediction: any) => {
      if (!prediction?.toPlace) {
        setError('No se encontró la ubicación seleccionada. Prueba con otra búsqueda.');
        return;
      }

      setLoading(true);
      setError(null);
      setSuggestionsOpen(false);
      try {
        const place = prediction.toPlace();
        if (place?.fetchFields) {
          await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'location'] });
        }

        const location = place?.location;
        const lat = typeof location?.lat === 'function' ? location.lat() : location?.lat;
        const lng = typeof location?.lng === 'function' ? location.lng() : location?.lng;
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          setError('No se pudo obtener coordenadas para la búsqueda. Ajusta el pin en el mapa.');
          return;
        }

        const point = { lat, lng };
        const displayName =
          typeof place?.displayName === 'string' ? place.displayName : place?.displayName?.text;
        const formattedAddress = place?.formattedAddress;
        setSelectedPlaceName(displayName || null);
        setSelectedPlaceAddress(formattedAddress || null);
        setCenter(point);
        setZoom(16);
        await commitMarker(point);
      } finally {
        setLoading(false);
      }
    },
    [commitMarker]
  );

  useEffect(() => {
    if (!open) return;
    if (Number.isFinite(currentLat) && Number.isFinite(currentLng)) {
      void commitMarker({ lat: currentLat as number, lng: currentLng as number });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const query = searchQuery.trim();
    if (!query) {
      setSuggestions([]);
      setSuggestionsOpen(false);
      setSuggestionsForQuery('');
      return;
    }
    if (isLikelyPlusCode(query)) {
      setSuggestions([]);
      setSuggestionsOpen(false);
      setSuggestionsForQuery('');
      return;
    }
    const fetchSuggestions = placesLib?.AutocompleteSuggestion?.fetchAutocompleteSuggestions;
    if (!fetchSuggestions) {
      setSuggestions([]);
      setSuggestionsOpen(false);
      return;
    }

    const currentFetchId = ++fetchIdRef.current;
    const handle = setTimeout(async () => {
      try {
        const result = await fetchSuggestions({
          input: query,
        });
        if (fetchIdRef.current !== currentFetchId) return;
        const next = (result?.suggestions || []).map((s: any) => s?.placePrediction).filter(Boolean).slice(0, 6);
        setSuggestions(next);
        setSuggestionsOpen(next.length > 0);
        setSuggestionsForQuery(query);
      } catch {
        if (fetchIdRef.current !== currentFetchId) return;
        setSuggestions([]);
        setSuggestionsOpen(false);
        setSuggestionsForQuery(query);
      }
    }, 250);

    return () => clearTimeout(handle);
  }, [open, placesLib, searchQuery]);

  const handleSearch = useCallback(async () => {
    const query = searchQuery.trim();
    if (!query) return;

    setError(null);
    setSelectedPlaceName(null);
    setSelectedPlaceAddress(null);
    setSuggestionsOpen(false);

    if (isLikelyPlusCode(query)) {
      await doPreview(query);
      return;
    }

    const prediction = suggestionsForQuery === query ? suggestions[0] : undefined;
    if (prediction) {
      await resolvePlacePrediction(prediction);
      return;
    }

    const fetchSuggestions = placesLib?.AutocompleteSuggestion?.fetchAutocompleteSuggestions;
    if (!fetchSuggestions) {
      setError('Búsqueda no disponible. Pega un Plus Code o ajusta el pin en el mapa.');
      return;
    }

    setLoading(true);
    try {
      const result = await fetchSuggestions({
        input: query,
      });

      const first = result?.suggestions?.[0]?.placePrediction;
      if (!first) {
        setError('No se encontraron resultados. Prueba con otro texto o ajusta el pin en el mapa.');
        return;
      }
      await resolvePlacePrediction(first);
    } finally {
      setLoading(false);
    }
  }, [doPreview, placesLib, resolvePlacePrediction, searchQuery, suggestions]);

  const applyDisabled = loading || !preview;

  return (
          <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Seleccionar ubicación</DialogTitle>
            <DialogContent sx={{ pt: 5, overflowY: 'visible', overflowX: 'visible' }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap', mb: 1, pt: 1 }}>
              <Box ref={searchAnchorRef} sx={{ flex: 1, minWidth: 240, position: 'relative' }}>
                <TextField
                  inputRef={searchInputRef}
                  label="Buscar dirección, lugar o Plus Code"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  fullWidth
                  disabled={loading}
                  helperText={
                    placesSearchAvailable
                      ? 'Puedes buscar por dirección, barrio, lugar conocido o Plus Code.'
                      : placesStatusMessage
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleSearch();
                    }
                    if (e.key === 'Escape') {
                      setSuggestionsOpen(false);
                    }
                  }}
                  onFocus={() => {
                    if (suggestions.length > 0) setSuggestionsOpen(true);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />

                <Popper
                  open={suggestionsOpen && suggestions.length > 0}
                  anchorEl={searchAnchorRef.current}
                  placement="bottom-start"
                  style={{ zIndex: 1500, width: searchAnchorRef.current?.clientWidth }}
                >
                  <ClickAwayListener onClickAway={() => setSuggestionsOpen(false)}>
                    <Paper elevation={4} sx={{ mt: 0.5, maxHeight: 280, overflow: 'auto' }}>
                      <List dense disablePadding>
                        {suggestions.map((prediction: any) => {
                          const textObj = prediction?.text;
                          const fullText =
                            typeof textObj === 'string' ? textObj : textObj?.text || prediction?.description || '';
                          const mainText =
                            prediction?.structuredFormat?.mainText?.text ||
                            prediction?.structuredFormat?.mainText ||
                            fullText;
                          const secondaryText =
                            prediction?.structuredFormat?.secondaryText?.text || prediction?.structuredFormat?.secondaryText || '';

                          return (
                            <ListItemButton
                              key={prediction?.placeId || fullText}
                              onClick={() => {
                                setSearchQuery(fullText);
                                void resolvePlacePrediction(prediction);
                              }}
                            >
                              <ListItemText primary={mainText} secondary={secondaryText || undefined} />
                            </ListItemButton>
                          );
                        })}
                      </List>
                    </Paper>
                  </ClickAwayListener>
                </Popper>
              </Box>

              <Button
                variant="contained"
                onClick={() => void handleSearch()}
                disabled={loading || !searchQuery.trim()}
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : undefined}
                sx={{ whiteSpace: 'nowrap', height: 56, mt: 0.5 }}
              >
                Buscar
              </Button>
            </Box>

        {(selectedPlaceName || selectedPlaceAddress || preview?.location) && (
          <Box sx={{ mb: 2 }}>
            {(selectedPlaceName || preview?.location) && (
              <Typography variant="subtitle2" sx={{ lineHeight: 1.2 }}>
                {selectedPlaceName || preview?.location}
              </Typography>
            )}
            {selectedPlaceName && selectedPlaceAddress && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {selectedPlaceAddress}
              </Typography>
            )}
          </Box>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Ajusta la ubicación arrastrando el pin o haciendo clic en el mapa.
        </Typography>

        <Box sx={{ height: 360, width: '100%', borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
          <Map
            center={center}
            zoom={zoom}
            style={{ width: '100%', height: '100%' }}
            gestureHandling="greedy"
            disableDefaultUI={false}
            mapId={mapId}
            onCameraChanged={(e: any) => {
              const nextCenter = e?.detail?.center;
              const nextZoom = e?.detail?.zoom;
              if (nextCenter && Number.isFinite(nextCenter.lat) && Number.isFinite(nextCenter.lng)) {
                setCenter({ lat: nextCenter.lat, lng: nextCenter.lng });
              }
              if (Number.isFinite(nextZoom)) {
                setZoom(nextZoom);
              }
            }}
            onClick={(e) => {
              const point = eventLatLngToLiteral(e);
              if (!point) return;
              setSelectedPlaceName(null);
              setSelectedPlaceAddress(null);
              setCenter(point);
              updateSelectedFromCoords(point);
              void commitMarker(point);
            }}
          >
            {mapId ? (
              <AdvancedMarker
                position={marker}
                draggable
                onDragEnd={(e) => {
                  const point = eventLatLngToLiteral(e);
                  if (!point) return;
                  setSelectedPlaceName(null);
                  setSelectedPlaceAddress(null);
                  setCenter(point);
                  updateSelectedFromCoords(point);
                  void commitMarker(point);
                }}
              >
                <Pin />
              </AdvancedMarker>
            ) : (
              <Marker
                position={marker}
                draggable
                onDragEnd={(e) => {
                  const point = eventLatLngToLiteral(e);
                  if (!point) return;
                  setSelectedPlaceName(null);
                  setSelectedPlaceAddress(null);
                  setCenter(point);
                  updateSelectedFromCoords(point);
                  void commitMarker(point);
                }}
              />
            )}
          </Map>
        </Box>

        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}

        {preview?.plus_code && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Plus Code (referencia): {preview.plus_code}
            {preview.location ? ` ${preview.location}` : ''}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => preview && onApply(preview, previewKey)}
          disabled={applyDisabled}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : undefined}
        >
          Confirmar ubicación
        </Button>
      </DialogActions>
    </Dialog>
  );
};
